import { Button, Form, FormField, Modal, Radio, TextArea } from "semantic-ui-react";
import { displayNetworkCodeOption, IParameter, NetworkCode } from "../../models/parameter";
import { useEffect, useMemo, useState } from "react";
import { toast } from "../..";
import { IParameterAnswer } from "../../models/parameterAnswer";
import { forceAnswerValue } from "../../services/parameterAnswerService";
import { getAnswerValues } from "./ImplementationMapsHelper";
import { useSelector } from "react-redux";
import { AppState } from "../../store/configureStore";
import { UserRole } from "../../actions/authentificationActions";

interface IProps {
    modalOpen: boolean;
    closeModal: () => void;
    parameterAnswer: IParameterAnswer;
    parameter: IParameter;
    networkCode: NetworkCode;
    refreshTrigger?: () => void;
}

export const ForceModal = ({
    modalOpen,
    closeModal,
    parameterAnswer,
    parameter,
    networkCode,
    refreshTrigger,
}: IProps) => {
    const [loading, setLoading] = useState(false);
    const [forcedValue, setForcedValue] = useState<number | undefined>();
    const user = useSelector((state: AppState) => state.user);

    useEffect(() => {
        setForcedValue(parameterAnswer.forcedAnswerValue);
    }, [modalOpen, parameterAnswer.forcedAnswerValue]);

    const updateForcedAnswer = async () => {
        if (forcedValue === undefined) {
            return;
        }
        try {
            setLoading(true);
            await forceAnswerValue(parameterAnswer.guid, forcedValue);
            toast("Forced answer updated", true, 1000);
        } catch {
            toast("Error updating forced answer", false, 1000);
        }
        setLoading(false);
    };

    const answerValues: string[] = useMemo(() => {
        if (!parameter) {
            return [];
        }
        return getAnswerValues(parameter);
    }, [parameter]);

    return (
        <Modal
            className="force-modal"
            open={modalOpen}
            onClose={() => {
                setForcedValue(undefined);
                closeModal();
            }}
            size="small"
            closeOnDimmerClick={false}
        >
            <Modal.Header>{parameterAnswer.countryName}</Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Field>
                        <label>
                            {displayNetworkCodeOption(networkCode)} - {parameter.name}
                        </label>
                        <div className={`answer-container color${parameterAnswer.answerValue}`}>
                            <input
                                value={answerValues[parameterAnswer.answerValue - 1]}
                                readOnly={true}
                            />
                        </div>
                    </Form.Field>

                    <Form.Field>
                        <label>Remark:</label>
                        <div className="answer-container">
                            <TextArea value={parameterAnswer.remark} readOnly={true} />
                        </div>
                    </Form.Field>
                    <Form.Field>
                        <label>Force color:</label>
                        {answerValues.map((answerValue, index) => (
                            <FormField key={index} className="parameter-answer-field">
                                <div className={`color-rectangle answer${index + 1}`}></div>
                                <Radio
                                    label={answerValue}
                                    name="radioGroup"
                                    value={index + 1}
                                    disabled={loading || user?.role !== UserRole.Admin}
                                    checked={
                                        (forcedValue ?? parameterAnswer.answerValue) === index + 1
                                    }
                                    onChange={() => setForcedValue(index + 1)}
                                />
                            </FormField>
                        ))}
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <div className="basic-actions">
                    <Button
                        onClick={() => {
                            setForcedValue(undefined);
                            closeModal();
                        }}
                        disabled={loading}
                        loading={loading}
                        content="Cancel"
                    />
                    <Button
                        primary
                        onClick={async () => {
                            await updateForcedAnswer();
                            refreshTrigger?.();
                            setForcedValue(undefined);
                            closeModal();
                        }}
                        disabled={
                            loading || forcedValue === undefined || user?.role !== UserRole.Admin
                        }
                        loading={loading}
                        content={"Force"}
                    />
                </div>
            </Modal.Actions>
        </Modal>
    );
};
